/**
 * Helpers for resolving which layout codes apply to a given page, used by the
 * SSR rendering in src/components/LayoutCodeSSR.tsx. There is no longer a
 * client-side counterpart — SSR is the only place layout codes are rendered.
 */

export type PageType = 'home' | 'section' | 'article';

export function detectPageType(pathname: string): PageType {
  if (pathname === '/' || pathname === '') return 'home';
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length >= 5) return 'article';
  if (segments.length >= 1) return 'section';
  return 'home';
}

export function resolvePageId(
  pathname: string,
  routePageIds: Record<string, string>
): string | null {
  if (routePageIds[pathname]) return routePageIds[pathname];
  const segments = pathname.split('/').filter(Boolean);
  for (let i = segments.length; i >= 1; i--) {
    const pattern =
      '/' +
      segments
        .slice(0, i - 1)
        .concat('{slug}')
        .join('/');
    if (routePageIds[pattern]) return routePageIds[pattern];
  }
  if (segments.length === 1 && routePageIds['/{slug}'])
    return routePageIds['/{slug}'];
  return null;
}

export function findSpecificPageMatch(
  specificPageCodes: any[],
  pathname: string,
  currentPageId: string | null
): any | null {
  if (!specificPageCodes || !Array.isArray(specificPageCodes)) return null;

  for (const entry of specificPageCodes) {
    if (!entry.pages || !Array.isArray(entry.pages)) continue;
    const matches = entry.pages.some((p: any) => {
      if (!p) return false;
      if (currentPageId && p.id && p.id === currentPageId) return true;
      if (p.name) {
        const slug = p.name.toLowerCase().replace(/\s+/g, '-');
        if (pathname === `/${slug}` || pathname.startsWith(`/${slug}/`))
          return true;
      }
      if (p.id && (pathname === `/${p.id}` || pathname.startsWith(`/${p.id}/`)))
        return true;
      return false;
    });
    if (matches) return entry;
  }
  return null;
}

/** Normalize a path/pattern: ensure leading slash, drop trailing slash (except root). */
export function normalizePath(p: string): string {
  if (!p) return '/';
  let s = p.startsWith('/') ? p : `/${p}`;
  if (s.length > 1 && s.endsWith('/')) s = s.slice(0, -1);
  return s;
}

function decodeSafe(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/**
 * Match a URL against a layout-code url pattern.
 *  - `/tamilnadu`   → exact path only
 *  - `/sports/*`    → exactly one level deep (`/sports/cricket`, not `/sports/cricket/ipl`)
 *  - `/sports/**`   → that path and any depth below it (`/sports`, `/sports/cricket/ipl`)
 *  - `/vijay*`      → prefix match (`/vijay`, `/vijay2026`, `/vijaybirthday`)
 */
export function matchUrlPattern(pattern: string, url: string): boolean {
  if (!pattern) return false;
  const u = normalizePath(decodeSafe(url));
  const raw = pattern.trim();

  if (raw.endsWith('/**')) {
    const base = normalizePath(decodeSafe(raw.slice(0, -3)));
    return u === base || u.startsWith(base + '/');
  }

  if (raw.endsWith('/*')) {
    const base = normalizePath(decodeSafe(raw.slice(0, -2)));
    if (!u.startsWith(base + '/')) return false;
    const rest = u.slice(base.length + 1);
    return rest.length > 0 && !rest.includes('/');
  }

  if (raw.endsWith('*')) {
    const prefix = normalizePath(decodeSafe(raw.slice(0, -1)));
    return u.startsWith(prefix);
  }

  return u === normalizePath(decodeSafe(raw));
}

export function findUrlBasedMatch(
  urlBasedCodes: any[],
  pathname: string
): any | null {
  if (!urlBasedCodes || !Array.isArray(urlBasedCodes)) return null;
  for (const entry of urlBasedCodes) {
    if (entry?.urlPattern && matchUrlPattern(entry.urlPattern, pathname)) {
      return entry;
    }
  }
  return null;
}

/**
 * Pick the code string to render for a device. No cross-device fallback:
 * when `hasSeparateMobileCode`/`hasSeparateMobileAmpCode` is off, `useMobile`
 * is already forced to false upstream so everyone gets the desktop
 * (headerCode/bodyCode) field — that's the single-code-for-both-devices
 * path. When it's on, each device strictly renders only its own field
 * (mobile-only or desktop-only content never leaks to the other device,
 * e.g. a Google Discover code authored mobile-only must stay mobile-only).
 */
function pickCode(
  desktop: string | null | undefined,
  mobile: string | null | undefined,
  useMobile: boolean
): string {
  return (useMobile ? mobile : desktop) || '';
}

export function getCodeStrings(
  code: Record<string, any>,
  pageType: PageType,
  isMobile: boolean,
  pathname: string = '/',
  currentPageId: string | null = null
): { headerCode: string; bodyCode: string } {
  const scope = code.scope;
  const useMobile = code.hasSeparateMobileCode && isMobile;

  if (code.hasSpecificPageCodes && code.specificPageCodes) {
    const match = findSpecificPageMatch(
      code.specificPageCodes,
      pathname,
      currentPageId
    );
    if (match) {
      return {
        headerCode: pickCode(
          match.headerCode,
          match.mobileHeaderCode,
          useMobile
        ),
        bodyCode: pickCode(match.bodyCode, match.mobileBodyCode, useMobile),
      };
    }
  }

  if (code.hasUrlBasedCodes && code.urlBasedCodes) {
    const match = findUrlBasedMatch(code.urlBasedCodes, pathname);
    if (match) {
      return {
        headerCode: pickCode(
          match.headerCode,
          match.mobileHeaderCode,
          useMobile
        ),
        bodyCode: pickCode(match.bodyCode, match.mobileBodyCode, useMobile),
      };
    }
  }

  if (scope === 'all_pages') {
    return {
      headerCode: pickCode(code.headerCode, code.mobileHeaderCode, useMobile),
      bodyCode: pickCode(code.bodyCode, code.mobileBodyCode, useMobile),
    };
  }

  const pageKey =
    pageType === 'home'
      ? 'homepage'
      : pageType === 'section'
        ? 'sectionPage'
        : 'articlePage';
  const cap = pageKey.charAt(0).toUpperCase() + pageKey.slice(1);

  return {
    headerCode: pickCode(
      code[`${pageKey}HeaderCode`],
      code[`mobile${cap}HeaderCode`],
      useMobile
    ),
    bodyCode: pickCode(
      code[`${pageKey}BodyCode`],
      code[`mobile${cap}BodyCode`],
      useMobile
    ),
  };
}

/**
 * Resolve BOTH the desktop and mobile AMP variants for a layout code,
 * honoring `isAmpEnabled`.
 *
 * Deliberately NOT device-driven: the AMP route's response is cached at a
 * shared CDN edge, so a User-Agent-based choice made at request time would
 * get baked into whichever device's request happened to warm the cache and
 * then served to every visitor of that URL regardless of their actual
 * device. Instead both variants are resolved here and the caller ships both
 * to the browser (inertly), where a small client-side script — the only
 * place that reliably knows the real device — picks the right one. That
 * keeps the HTML identical (and safely cacheable) no matter who requests it.
 *
 * When `hasSeparateMobileAmpCode` is off, mobile mirrors desktop (single
 * code for both devices, same as before this dual-variant split existed).
 */
export function getAmpCodeStringsBothDevices(code: Record<string, any>): {
  desktopHeaderCode: string;
  mobileHeaderCode: string;
  desktopBodyCode: string;
  mobileBodyCode: string;
} {
  if (!code.isAmpEnabled) {
    return {
      desktopHeaderCode: '',
      mobileHeaderCode: '',
      desktopBodyCode: '',
      mobileBodyCode: '',
    };
  }
  const hasSeparate = !!code.hasSeparateMobileAmpCode;
  const desktopHeaderCode = code.ampHeaderCode || '';
  const desktopBodyCode = code.ampBodyCode || '';
  return {
    desktopHeaderCode,
    mobileHeaderCode: hasSeparate
      ? code.mobileAmpHeaderCode || ''
      : desktopHeaderCode,
    desktopBodyCode,
    mobileBodyCode: hasSeparate
      ? code.mobileAmpBodyCode || ''
      : desktopBodyCode,
  };
}

/**
 * Best-effort mobile detection from a User-Agent string.
 * Server uses this to pick the mobile variant during SSR — the client
 * still re-evaluates via window-width on hydration and re-injects on mismatch.
 */
export function isMobileFromUA(userAgent: string | null | undefined): boolean {
  if (!userAgent) return false;
  return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    userAgent
  );
}

export interface ActiveLayoutCode {
  slug: string;
  headerCode: string;
  bodyCode: string;
  /** CMS-flagged as an ad code — rendered with the same DOM markers/hide-rules as other ad types. */
  isAdCode: boolean;
}

/**
 * Filter + resolve the layout codes that should render on a given page.
 * Skips inactive codes and codes that produce no head/body content.
 */
export function getActiveLayoutCodes(
  allCodes: Record<string, any>,
  pathname: string,
  isMobile: boolean,
  routePageIds: Record<string, string>
): ActiveLayoutCode[] {
  const pageType = detectPageType(pathname);
  const currentPageId = resolvePageId(pathname, routePageIds);
  const out: ActiveLayoutCode[] = [];
  for (const [slug, code] of Object.entries(allCodes)) {
    if (code.isActive === false || code.isActive === 'false' || !code.isActive)
      continue;
    const { headerCode, bodyCode } = getCodeStrings(
      code,
      pageType,
      isMobile,
      pathname,
      currentPageId
    );
    if (!headerCode && !bodyCode) continue;
    out.push({ slug, headerCode, bodyCode, isAdCode: !!code.isAdCode });
  }
  return out;
}

export interface ActiveAmpLayoutCodeBothDevices {
  slug: string;
  desktopHeaderCode: string;
  mobileHeaderCode: string;
  desktopBodyCode: string;
  mobileBodyCode: string;
}

/**
 * Filter + resolve (both device variants of) the layout codes that should
 * render on an AMP page. Only codes with `isAmpEnabled` contribute;
 * everything else (scope, specificPageCodes, urlBasedCodes) is irrelevant
 * on AMP pages. See `getAmpCodeStringsBothDevices` for why this returns
 * both variants instead of picking one.
 */
export function getActiveAmpLayoutCodes(
  allCodes: Record<string, any>
): ActiveAmpLayoutCodeBothDevices[] {
  const out: ActiveAmpLayoutCodeBothDevices[] = [];
  for (const [slug, code] of Object.entries(allCodes)) {
    if (code.isActive === false || code.isActive === 'false' || !code.isActive)
      continue;
    if (!code.isAmpEnabled) continue;
    const {
      desktopHeaderCode,
      mobileHeaderCode,
      desktopBodyCode,
      mobileBodyCode,
    } = getAmpCodeStringsBothDevices(code);
    if (
      !desktopHeaderCode &&
      !mobileHeaderCode &&
      !desktopBodyCode &&
      !mobileBodyCode
    )
      continue;
    out.push({
      slug,
      desktopHeaderCode,
      mobileHeaderCode,
      desktopBodyCode,
      mobileBodyCode,
    });
  }
  return out;
}

export interface ScriptPart {
  src?: string;
  async?: boolean;
  defer?: boolean;
  crossOrigin?: string;
  type?: string;
  content?: string;
}

/**
 * Split a layout-code HTML string into (a) non-script HTML and (b) extracted
 * <script> blocks. Used by SSR rendering: scripts are emitted as JSX <script>
 * elements (so they execute), the non-script HTML is rendered via
 * dangerouslySetInnerHTML on a wrapper element.
 */
export function parseLayoutCodeHtml(html: string): {
  nonScriptHtml: string;
  scripts: ScriptPart[];
} {
  if (!html || !html.trim()) return { nonScriptHtml: '', scripts: [] };
  const scripts: ScriptPart[] = [];
  const scriptRegex = /<script\b([^>]*)>([\s\S]*?)<\/script>/gi;
  const nonScriptHtml = html.replace(
    scriptRegex,
    (_match, attrStr: string, content: string) => {
      scripts.push({ ...parseScriptAttributes(attrStr), content });
      return '';
    }
  );
  return { nonScriptHtml, scripts };
}

function parseScriptAttributes(attrStr: string): Omit<ScriptPart, 'content'> {
  const out: Omit<ScriptPart, 'content'> = {};
  const attrRegex = /(\w[\w-]*)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|(\S+)))?/g;
  let m: RegExpExecArray | null;
  while ((m = attrRegex.exec(attrStr)) !== null) {
    const name = m[1].toLowerCase();
    const value = m[2] ?? m[3] ?? m[4] ?? '';
    if (name === 'src') out.src = value;
    else if (name === 'async') out.async = true;
    else if (name === 'defer') out.defer = true;
    else if (name === 'crossorigin') out.crossOrigin = value || 'anonymous';
    else if (name === 'type') out.type = value;
  }
  return out;
}

/**
 * Layout codes that are hardcoded here rather than coming from the CMS
 * layout-code system.
 *
 * WHY THIS IS A FUNCTION AND NOT AN INLINE PUSH IN layout.tsx: it used to be
 * appended to `ssrLayoutCodes` directly after `getActiveLayoutCodes()`, which
 * made it invisible to `LayoutCodeRefresh`. That component tears down every
 * `[data-layout-code-ssr-container]` element and then re-injects whatever
 * `getActiveLayoutCodes()` returns — so the hardcoded block got **deleted and
 * never restored**, leaving GPT with a slot whose div no longer existed
 * ("could not find div with id ... in DOM for slot /3167926/DIN_Hybrid_InImage")
 * and an in-image ad that could never render. Both the SSR path and the client
 * re-inject path now read the set from here, so they cannot drift.
 *
 * Article pages only (same rule as the "articlePage" layout-code scope), so the
 * GPT slot is never defined or displayed on home/section/other pages.
 */
export function getHardcodedLayoutCodes(pathname: string): ActiveLayoutCode[] {
  if (detectPageType(pathname) !== 'article') return [];

  // RTB_Insight_Internal — fed through the same LayoutCodeSSRHead/Body
  // components as CMS codes so it gets the exact same execution guarantees
  // (literal <script> tags parsed with the initial HTML, not deferred to
  // post-hydration like next/script's <Script strategy="afterInteractive">
  // would do) and participates in the centralized enableServices() call.
  return [
    {
      slug: 'rtb-insight-internal',
      isAdCode: true,
      headerCode: `<script>
  window.googletag = window.googletag || {cmd: []};
  googletag.cmd.push(function() {
    googletag.defineSlot('/3167926/DIN_Hybrid_InImage', [1, 1], 'div-gpt-ad-1789558718091-0').addService(googletag.pubads());
    googletag.pubads().enableSingleRequest();
    googletag.enableServices();
  });
</script>`,
      bodyCode: `<div id='div-gpt-ad-1789558718091-0'>
  <script>
    googletag.cmd.push(function() { googletag.display('div-gpt-ad-1789558718091-0'); });
  </script>
</div>`,
    },
  ];
}
